import {
  ChallengeEventType,
  Match,
  MatchEventType,
} from "../../../../directus/collections";

import { AggregatedMatchesSummary, SceneStatsSummary } from "../../../../hooks";
import { GamePathStatistics } from "../../../../libs/gameAnalysis/paths";
import { TimelineEntry } from "../../../../libs/gameAnalysis/timeline";
import { MatchFilter } from "../../../../libs/statsFilters";
import {
  CommunicationChannel,
  MessagePayload,
  ServiceWorkerAppError,
} from "../../../../libs/workers/types";
import {
  MatchEnigmaDetailedSummary,
  MatchEnigmaIds,
  MatchEnigmaSummary,
} from "../../../../services/enigmaStats.service";
import { MatchCityEscapeSummary } from "../../../../services/matches";
import { ChallengeDetails } from "../../../../types/cityEscape";
import { getDatasource } from "./datasource";
import { TimePartials } from "./service";

export const StatsQueryTags = {
  STATS_MODULE_BASED: "SW::STATS" as const,
} as const;

export const StatsQuery = {
  ENIGMA_TOTAL_MATCHES: "ENIGMA_TOTAL_MATCHES",
  TOTAL_ENIGMA_STARTS: "TOTAL_ENIGMA_STARTS",
  SUCCESS_RATE: "SUCCESS_RATE",
  ALL_MATCHES: "ALL_MATCHES",
  ALL_MATCHES_ENIGMAS: "ALL_MATCHES_ENIGMAS",
  ALL_MATCHES_IDS: "ALL_MATCHES_IDS",
  SUCCESSFUL_MATCHES: "SUCCESSFUL_MATCHES",
  FAILURE_RATE: "FAILURE_RATE",
  FAILING_MATCHES: "FAILING_MATCHES",
  DROP_RATE: "DROP_RATE",
  DROPPING_MATCHES: "DROPPING_MATCHES",
  BUGS_RATE: "BUGS_RATE",
  BUGGED_MATCHES: "BUGGED_MATCHES",
  NO_HINTS_PROVIDED_RATE: "NO_HINTS_PROVIDED_RATE",
  AVERAGE_TIME: "AVERAGE_TIME",
  AVERAGE_ERRORS: "AVERAGE_ERRORS",
  AVERAGE_HINTS: "AVERAGE_HINTS",
  PATHS: "PATHS",
  MATCH_HINTS: "MATCH_HINTS",
  MATCHES_TIMELINES: "MATCHES_TIMELINES",
  CHALLENGE_DATA: "CHALLENGE_DATA",
  MATCH_PLAY_RATE: "MATCH_PLAY_RATE",
  MATCH_COMPLETION_RATE: "MATCH_COMPLETION_RATE",
  MATCH_AVG_DURATION_FROM_PURCHASE_TO_COMPLETION:
    "MATCH_AVG_DURATION_FROM_PURCHASE_TO_COMPLETION",
  MATCH_AVG_DURATION_FROM_CREATION_TO_COMPLETION:
    "MATCH_AVG_DURATION_FROM_CREATION_TO_COMPLETION",
  MATCH_AVG_DURATION_FROM_FIRST_TO_LAST_ENIGMA:
    "MATCH_AVG_DURATION_FROM_FIRST_TO_LAST_ENIGMA",
  MATCH_OPEN_RATE: "MATCH_OPEN_RATE",
  MATCHES_ENIGMA_SUMMARIES: "MATCHES_ENIGMA_SUMMARIES",
  MATCHES_CITY_ESCAPE_SUMMARIES: "MATCHES_CITY_ESCAPE_SUMMARIES",
  CITY_ESCAPE_SUMMARIES: "CITY_ESCAPE_SUMMARIES",
  PRODUCT_SUMMARIES: "PRODUCT_SUMMARIES",
  SCENE_STATS_SUMMARY: "SCENE_STATS_SUMMARY",
  AVG_TIME_TO_EVENT: "AVG_TIME_TO_EVENT",
  CITY_ESCAPE_AVG_RATING: "CITY_ESCAPE_AVG_RATING",
  AVG_TIME_TO_ENIGMA_EVENT: "AVG_TIME_TO_ENIGMA_EVENT",
  MATCH_COUNT: "MATCH_COUNT",
  TOTAL_WITHDRAWALS_EVENTS: "TOTAL_WITHDRAWALS_EVENTS",
} as const;

export type StatsQueryType = (typeof StatsQuery)[keyof typeof StatsQuery];

export type BaseQueryPayload = {
  cityEscapeIds?: string | string[] | null;
  enigmaId?: string;
  filters?: MatchFilter;
};

export type GlobalQueryPayload = BaseQueryPayload & {
  productIds?: string | string[] | number | number[] | null;
};

export type StatsCallbackMeta<P, R> = {
  props: P;
  result: R;
};

export type DynamicStatsQuery = {
  keys: string[];
  values: string[];
};

export type StatsQueryMeta = {
  [StatsQuery.ENIGMA_TOTAL_MATCHES]: {
    props: BaseQueryPayload;
    result: number;
  };
  [StatsQuery.TOTAL_ENIGMA_STARTS]: { props: BaseQueryPayload; result: number };
  [StatsQuery.SUCCESS_RATE]: { props: BaseQueryPayload; result: number };
  [StatsQuery.ALL_MATCHES]: { props: GlobalQueryPayload; result: Match[] };
  [StatsQuery.ALL_MATCHES_ENIGMAS]: {
    props: BaseQueryPayload;
    result: MatchEnigmaSummary[];
  };
  [StatsQuery.ALL_MATCHES_IDS]: {
    props: GlobalQueryPayload;
    result: string[];
  };
  [StatsQuery.SUCCESSFUL_MATCHES]: {
    props: BaseQueryPayload;
    result: MatchEnigmaSummary[];
  };
  [StatsQuery.FAILURE_RATE]: { props: BaseQueryPayload; result: number };
  [StatsQuery.FAILING_MATCHES]: {
    props: BaseQueryPayload;
    result: MatchEnigmaSummary[];
  };
  [StatsQuery.DROP_RATE]: { props: BaseQueryPayload; result: number };
  [StatsQuery.DROPPING_MATCHES]: {
    props: BaseQueryPayload;
    result: MatchEnigmaSummary[];
  };
  [StatsQuery.BUGS_RATE]: { props: BaseQueryPayload; result: number };
  [StatsQuery.BUGGED_MATCHES]: {
    props: BaseQueryPayload;
    result: { matchId: string; targetId: string }[];
  };
  [StatsQuery.NO_HINTS_PROVIDED_RATE]: {
    props: BaseQueryPayload;
    result: number;
  };
  [StatsQuery.AVERAGE_TIME]: { props: BaseQueryPayload; result: number };
  [StatsQuery.AVERAGE_ERRORS]: { props: BaseQueryPayload; result: number };
  [StatsQuery.AVERAGE_HINTS]: { props: BaseQueryPayload; result: number };
  [StatsQuery.PATHS]: { props: BaseQueryPayload; result: GamePathStatistics[] };
  [StatsQuery.MATCH_HINTS]: {
    props: BaseQueryPayload;
    result: Record<number, number>;
  };
  [StatsQuery.MATCHES_TIMELINES]: {
    props: BaseQueryPayload;
    result: TimelineEntry[][];
  };
  [StatsQuery.CHALLENGE_DATA]: {
    props: {
      cityEscapeId?: string;
      enigmaId?: string;
    };
    result: ChallengeDetails | null;
  };
  [StatsQuery.MATCH_PLAY_RATE]: { props: GlobalQueryPayload; result: number };
  [StatsQuery.MATCH_COMPLETION_RATE]: {
    props: GlobalQueryPayload;
    result: number;
  };
  [StatsQuery.MATCH_AVG_DURATION_FROM_PURCHASE_TO_COMPLETION]: {
    props: GlobalQueryPayload;
    result: number;
  };
  [StatsQuery.MATCH_AVG_DURATION_FROM_CREATION_TO_COMPLETION]: {
    props: GlobalQueryPayload;
    result: number;
  };
  [StatsQuery.MATCH_AVG_DURATION_FROM_FIRST_TO_LAST_ENIGMA]: {
    props: GlobalQueryPayload;
    result: number;
  };
  [StatsQuery.MATCH_OPEN_RATE]: { props: GlobalQueryPayload; result: number };
  [StatsQuery.MATCHES_ENIGMA_SUMMARIES]: {
    props: { entries: MatchEnigmaIds[] };
    result: (MatchEnigmaDetailedSummary | null)[];
  };
  [StatsQuery.MATCHES_CITY_ESCAPE_SUMMARIES]: {
    props: {
      matchIds?: string[];
      cityEscapeIds?: string | string[] | null;
      filters?: MatchFilter;
    };
    result: MatchCityEscapeSummary[];
  };
  [StatsQuery.CITY_ESCAPE_SUMMARIES]: {
    props: GlobalQueryPayload;
    result: AggregatedMatchesSummary[];
  };
  [StatsQuery.PRODUCT_SUMMARIES]: {
    props: GlobalQueryPayload;
    result: AggregatedMatchesSummary[];
  };
  [StatsQuery.SCENE_STATS_SUMMARY]: {
    props: {
      cityEscapeId?: string | null;
      sceneId?: string;
      filters?: MatchFilter;
    };
    result: SceneStatsSummary | undefined;
  };
  [StatsQuery.AVG_TIME_TO_EVENT]: {
    props: {
      cityEscapeId?: string | null;
      targetId?: string;
      filters?: MatchFilter;
      eventType?: MatchEventType | MatchEventType[];
      value?: any;
    };
    result: TimePartials & { playRate: number };
  };
  [StatsQuery.AVG_TIME_TO_ENIGMA_EVENT]: {
    props: {
      cityEscapeId?: string | null;
      targetId?: string;
      filters?: MatchFilter;
      eventType?: ChallengeEventType | ChallengeEventType[];
      value?: any;
    };
    result: TimePartials & { playRate: number };
  };
  [StatsQuery.CITY_ESCAPE_AVG_RATING]: {
    props: BaseQueryPayload;
    result: number;
  };
  [StatsQuery.MATCH_COUNT]: { props: GlobalQueryPayload; result: number };
  [StatsQuery.TOTAL_WITHDRAWALS_EVENTS]: {
    props: BaseQueryPayload;
    result: { totalWithdrawals: number };
  };
};

export type CallbackContext = {
  client: CommunicationChannel;
  datasource: ReturnType<typeof getDatasource>;
  worker: ServiceWorkerGlobalScope;
};

export type StatsQueryProps<T extends StatsQueryType> =
  StatsQueryMeta[T] extends { props: infer P } ? P : any;

export type StatsQueryResult<T extends StatsQueryType> =
  StatsQueryMeta[T] extends { result: infer R } ? R : any;

export type StatsQueryMessageProps<T extends StatsQueryType> = MessagePayload<
  T,
  StatsQueryProps<T>
> & { data: StatsQueryProps<T> };

export type StatsCallbackProps<T extends StatsQueryType> = CallbackContext &
  StatsQueryMessageProps<T>;

export type MessageListenerProps<T extends StatsQueryType> = CallbackContext & {
  event: ExtendableMessageEvent | MessageEvent<StatsQueryMessageProps<T>>;
};

export type StatsQueryMap = {
  [K in StatsQueryType]: (
    props: StatsCallbackProps<K>
  ) => StatsQueryResult<K> | Promise<StatsQueryResult<K>>;
};

export const StatsQueryErrors = {
  INVALID_QUERY: "INVALID_QUERY" as const,
    DATA_SOURCE_NOT_READY: "DATA_SOURCE_NOT_READY" as const,
} as const;

export type StatsQueryErrorType = keyof typeof StatsQueryErrors;

export class StatsQueryError extends ServiceWorkerAppError {
  request: { id: string; type: string; timestamp: number } | null;

  constructor(
    type: StatsQueryErrorType,
    message: string,
    request: { id: string; type: string; timestamp: number } | null = null
  ) {
    super(type, message);
    this.name = "StatsQueryError";
    this.request = request;
  }
}
